import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

const DEST_POSTCODE = "SL4 1NJ";
const CASTLE_COORDS = { lat: 51.4839, lon: -0.6044 };
const CASTLE_TICKETS = {
  adult: { label: "Adult", advance: 32, walkup: 36 },
  youngPerson: { label: "Young Person (18-24)", advance: 21, walkup: 24 },
  child: { label: "Child (5-17)", advance: 16, walkup: 18 },
  disabled: { label: "Disabled Person", advance: 16, walkup: 18 },
  companion: { label: "Access companion", advance: 0, walkup: 0 },
  under5: { label: "Under 5", advance: 0, walkup: 0 },
  benefits: { label: "Benefit ticket", advance: 1, walkup: 1 },
};

const PARKING_OPTIONS = {
  riverStreet: {
    label: "River Street car park",
    shortLabel: "River Street",
    postcode: "SL4 1QT",
    note: "Closest option near the Castle. Charges apply 9am-9pm.",
    tariffs: [
      { maxHours: 1, price: 7 },
      { maxHours: 2, price: 11 },
      { maxHours: 3, price: 14 },
      { maxHours: 4, price: 19 },
      { maxHours: 5, price: 22 },
      { maxHours: Infinity, price: 30 },
    ],
  },
  almaRoad: {
    label: "Alma Road car park",
    shortLabel: "Alma Road",
    postcode: "SL4 3HY",
    note: "Further away, usually cheaper. Charges apply 9am-9pm.",
    tariffs: [
      { maxHours: 1, price: 2.3 },
      { maxHours: 2, price: 4.6 },
      { maxHours: 3, price: 7 },
      { maxHours: 4, price: 10 },
      { maxHours: 5, price: 15 },
      { maxHours: Infinity, price: 19 },
    ],
  },
  manual: {
    label: "Manual parking budget",
    shortLabel: "Manual",
    postcode: null,
    note: "Use your own expected parking cost.",
    tariffs: [],
  },
};

const WINDSOR_FAQS = [
  {
    question: "How do I get to Windsor Castle?",
    answer:
      "Use the planner to compare driving, taxi and rail from your postcode to Windsor Castle. It checks Windsor rail gateways, local taxi costs, parking options and a leave-home time for your visit date.",
  },
  {
    question: "How much is a ticket at Windsor Castle?",
    answer:
      "Advance Windsor Castle tickets are listed as adult GBP32, young person GBP21, child GBP16 and disabled person GBP16. Under 5s and access companions are free, and each person should still have a ticket.",
  },
  {
    question: "Is Windsor Castle open today?",
    answer:
      "Windsor Castle is normally closed on Tuesdays and Wednesdays, with some additional royal or operational closures. The planner checks the selected date and shows opening time, last admission and closing time where available.",
  },
  {
    question: "How much does a taxi to Windsor Castle cost?",
    answer:
      "Taxi cost depends on pickup postcode, passenger count, bags and time. The planner uses CheapCabGrab live taxi preview pricing where available, including checks from Windsor stations, hotels and Heathrow.",
  },
  {
    question: "How much does rail to Windsor Castle cost?",
    answer:
      "Rail cost depends on the route and fare returned for your date. The planner compares live rail routes via Windsor & Eton Riverside and Slough where available, then adds the walk or local taxi transfer to the Castle.",
  },
  {
    question: "How much does parking near Windsor Castle cost?",
    answer:
      "The drive estimate can include River Street car park near the Castle or Alma Road as a cheaper option. River Street is typically higher priced, while Alma Road is further away but cheaper for longer stays.",
  },
  {
    question: "What is the weather like at Windsor Castle?",
    answer:
      "When a visit date is selected, the planner checks the forecast for Windsor Castle and shows a simple weather summary for planning walking, rail and taxi choices.",
  },
  {
    question: "How much is a taxi from Windsor station or a Windsor hotel to the Castle?",
    answer:
      "The nearby taxi section checks live taxi preview fares from Windsor & Eton Central, Windsor & Eton Riverside, Slough Station, central Windsor hotels, LEGOLAND and Heathrow Terminal 5 to Windsor Castle.",
  },
];

const CASTLE_EXCEPTIONS = {
  "2026-05-14": { is_open: true, open_time: "10:00", last_admission_time: "15:30", close_time: "16:45", event_label: "Castle closes early" },
  "2026-06-14": { is_open: false, event_label: "Castle closed" },
  "2026-06-15": { is_open: false, event_label: "Castle closed" },
};

const LOCAL_POINTS = [
  {
    id: "windsor-central",
    label: "Windsor & Eton Central",
    pickup: "SL4 1PJ",
    duration: "5-10 mins walk",
    note: "Best town-centre station for the Castle.",
    kind: "Station",
  },
  {
    id: "windsor-riverside",
    label: "Windsor & Eton Riverside",
    pickup: "SL4 1QG",
    duration: "8-12 mins walk",
    note: "Useful for South Western Railway routes.",
    kind: "Station",
  },
  {
    id: "slough-station",
    label: "Slough Station",
    pickup: "SL1 1XW",
    duration: "10-20 mins taxi or short rail hop",
    note: "Good interchange if the direct Windsor route is awkward.",
    kind: "Station",
  },
  {
    id: "castle-hotel-windsor",
    label: "The Castle Hotel Windsor",
    pickup: "SL4 1LJ",
    duration: "2-5 mins walk",
    note: "Very close to the Castle; taxi mainly helps with bags or mobility.",
    kind: "Hotel",
  },
  {
    id: "macdonald-windsor",
    label: "Macdonald Windsor Hotel",
    pickup: "SL4 1LH",
    duration: "2-5 mins walk",
    note: "Central High Street hotel opposite the Castle area.",
    kind: "Hotel",
  },
  {
    id: "sir-christopher-wren",
    label: "Sir Christopher Wren Hotel",
    pickup: "SL4 1PX",
    duration: "8-12 mins walk",
    note: "Riverside hotel near Windsor & Eton Riverside station.",
    kind: "Hotel",
  },
  {
    id: "travelodge-windsor-central",
    label: "Travelodge Windsor Central",
    pickup: "SL4 1TG",
    duration: "5-10 mins walk",
    note: "Town-centre hotel near Windsor Royal shopping and Central station.",
    kind: "Hotel",
  },
  {
    id: "holiday-inn-express-windsor",
    label: "Holiday Inn Express Windsor",
    pickup: "SL4 3HD",
    duration: "15-20 mins walk or short taxi",
    note: "Useful taxi check for Alma Road stays.",
    kind: "Hotel",
  },
  {
    id: "legoland-resort-hotel",
    label: "LEGOLAND Windsor Resort Hotel",
    pickup: "SL4 4AY",
    duration: "10-20 mins taxi",
    note: "Helpful if combining Windsor Castle with a LEGOLAND stay.",
    kind: "Hotel",
  },
  {
    id: "heathrow-t5",
    label: "Heathrow Terminal 5",
    pickup: "TW6 2GB",
    duration: "20-35 mins taxi",
    note: "Simple option for airport visitors heading straight to Windsor.",
    kind: "Airport",
  },
];

const defaultDateTime = () => {
  const now = new Date();
  const target = new Date(now);
  target.setDate(now.getDate() + 30);
  target.setHours(10, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
};

const normalizePostcode = (pc) => String(pc || "").toUpperCase().replace(/\s+/g, " ").trim();
const money = (v, decimals = 0) => (v == null || Number.isNaN(Number(v)) ? "-" : `£${Number(v).toFixed(decimals)}`);
const formatTime = (t) => (t ? String(t).slice(0, 5) : "-");
const rangeText = (min, max) => (min === max ? money(min) : `${money(min)} - ${money(max)}`);

const formatDisplayDate = (date) => {
  if (!date) return "";
  const d = new Date(`${date}T12:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

const plural = (count, singular, pluralLabel = `${singular}s`) => {
  const n = Number(count || 0);
  if (!n) return null;
  return `${n} ${n === 1 ? singular : pluralLabel}`;
};

const localCastleDay = (date) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date || ""))) return null;
  if (CASTLE_EXCEPTIONS[date]) return { visit_date: date, ...CASTLE_EXCEPTIONS[date] };
  const d = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const weekday = d.getUTCDay();
  const closed = weekday === 2 || weekday === 3;
  const month = Number(date.slice(5, 7));
  const winter = month === 11 || month === 12 || month === 1 || month === 2;
  return {
    visit_date: date,
    is_open: !closed,
    open_time: closed ? null : "10:00",
    last_admission_time: closed ? null : winter ? "15:00" : "16:00",
    close_time: closed ? null : winter ? "16:15" : "17:15",
    event_label: closed ? "Closed on Tuesdays and Wednesdays" : null,
  };
};

const localCastleMonth = (month) => {
  if (!/^\d{4}-\d{2}$/.test(String(month || ""))) return { month, days: [] };
  const days = [];
  const start = new Date(`${month}-01T12:00:00Z`);
  const cursor = new Date(start);
  while (!Number.isNaN(cursor.getTime()) && cursor.toISOString().slice(0, 7) === month) {
    days.push(localCastleDay(cursor.toISOString().slice(0, 10)));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return { month, days };
};

const addMinutes = (date, mins) => {
  if (!date || Number.isNaN(date.getTime())) return null;
  return new Date(date.getTime() + Number(mins || 0) * 60000);
};

const subtractMinutes = (date, mins) => addMinutes(date, -Number(mins || 0));

const formatClock = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const parseVisitClock = (date, clock) => {
  if (!date || !clock) return null;
  const d = new Date(`${date}T${String(clock).slice(0, 5)}:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const ticketTotal = (tickets, priceType = "advance") => {
  const prices = {
    adult: CASTLE_TICKETS.adult[priceType],
    youngPerson: CASTLE_TICKETS.youngPerson[priceType],
    child: CASTLE_TICKETS.child[priceType],
    disabled: CASTLE_TICKETS.disabled[priceType],
    under5: 0,
  };
  return (
    Number(tickets.adults || 0) * prices.adult +
    Number(tickets.youngPeople || 0) * prices.youngPerson +
    Number(tickets.children || 0) * prices.child +
    Number(tickets.disabled || 0) * prices.disabled
  );
};

const estimateDriveCost = (distanceMiles, parkingCost, returnSameDay) => {
  const legs = returnSameDay ? 2 : 1;
  const fuelAndWear = Number(distanceMiles || 0) * legs * 0.42;
  return Math.round(fuelAndWear + Number(parkingCost || 0));
};

const estimateRoadCost = (distanceMiles, returnSameDay) => {
  const legs = returnSameDay ? 2 : 1;
  return Math.round(Number(distanceMiles || 0) * legs * 0.42);
};

const parkingCostForHours = (parkingKey, hours, manualCost) => {
  if (parkingKey === "manual") return Math.max(0, Number(manualCost || 0));
  const option = PARKING_OPTIONS[parkingKey] || PARKING_OPTIONS.riverStreet;
  const stayHours = Math.max(1, Number(hours || 5));
  const tier = option.tariffs.find((row) => stayHours <= row.maxHours);
  return tier ? Number(tier.price) : 0;
};

const estimateTrainCost = (distanceMiles, people) => {
  const heads = Math.max(1, Number(people || 1));
  const base = Math.max(7, Math.min(38, Number(distanceMiles || 18) * 0.55));
  return Math.round(base * heads);
};

const estimateTaxiFallback = (distanceMiles, returnSameDay) => {
  const legs = returnSameDay ? 2 : 1;
  const oneWay = 18 + Number(distanceMiles || 12) * 2.05;
  return Math.round(oneWay * legs);
};

const selectLiveTaxiPreview = (data) => {
  const vehicles = Array.isArray(data?.vehicles) ? data.vehicles : [];
  const canLiveBook = Boolean(data?.eligibility?.can_live_book) && !Boolean(data?.eligibility?.enquiry_only);
  const priced = vehicles
    .filter((v) => v?.price != null)
    .map((v) => ({ ...v, price: Number(v.price) }))
    .sort((a, b) => a.price - b.price);
  const cheapestLive = priced.find((v) => String(v.book_mode || "").toLowerCase() === "live") || null;
  if (!canLiveBook || !cheapestLive) return null;
  return {
    ...cheapestLive,
    price: Number(cheapestLive.price),
    source: "quote-preview-v2",
    liveBookable: true,
  };
};

export default function WindsorCastleBudgetPlanner() {
  const isMobile = typeof window !== "undefined" ? window.innerWidth <= 640 : false;
  const [form, setForm] = useState({
    pickup: "SW1A 1AA",
    datetime: defaultDateTime(),
    adults: 2,
    youngPeople: 0,
    children: 1,
    under5: 0,
    disabled: 0,
    bags: 0,
    returnSameDay: true,
    transport: "compare",
    parkingOption: "riverStreet",
    parkingHours: 5,
    parkingCost: 22,
  });
  const [applied, setApplied] = useState(form);
  const [dayData, setDayData] = useState(null);
  const [monthData, setMonthData] = useState({ month: "", days: [] });
  const [distanceMiles, setDistanceMiles] = useState(22);
  const [taxiQuote, setTaxiQuote] = useState(null);
  const [liveRailPlan, setLiveRailPlan] = useState(null);
  const [weatherSummary, setWeatherSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [localTaxiQuotes, setLocalTaxiQuotes] = useState({});
  const [localTransportOpen, setLocalTransportOpen] = useState(false);
  const localTransportRef = useRef(null);

  const selectedDate = String(applied.datetime || "").slice(0, 10);
  const visitTime = String(applied.datetime || "").slice(11, 16) || "10:00";
  const people = Number(applied.adults || 0) + Number(applied.youngPeople || 0) + Number(applied.children || 0) + Number(applied.under5 || 0) + Number(applied.disabled || 0);
  const hasPendingChanges = JSON.stringify(form) !== JSON.stringify(applied);
  const formPeople = Number(form.adults || 0) + Number(form.youngPeople || 0) + Number(form.children || 0) + Number(form.under5 || 0) + Number(form.disabled || 0);
  const ticketPartySummary = [
    plural(applied.adults, "adult"),
    plural(applied.youngPeople, "young person", "young people"),
    plural(applied.children, "child", "children"),
    plural(applied.disabled, "disabled ticket"),
    plural(applied.under5, "under 5", "under 5s"),
  ].filter(Boolean).join(" + ");
  const hiddenOptionsSummary = [
    `${formPeople || 1} visitor${formPeople === 1 ? "" : "s"}`,
    form.transport === "compare" ? "Compare all modes" : form.transport === "drive" ? "Drive" : form.transport === "taxi" ? "Taxi" : "Train",
    form.returnSameDay ? "Return same day" : "One-way visit",
    `${PARKING_OPTIONS[form.parkingOption]?.shortLabel || "Parking"} ${money(parkingCostForHours(form.parkingOption, form.parkingHours, form.parkingCost))}`,
  ].join(" • ");

  useEffect(() => {
    if (!selectedDate) return;
    const month = selectedDate.slice(0, 7);
    if (monthData.month === month) return;
    let cancelled = false;
    api
      .get("/api/windsor-castle/month", { params: { month } })
      .then((res) => {
        if (!cancelled) setMonthData(res.data || { month, days: [] });
      })
      .catch(() => {
        if (!cancelled) setMonthData(localCastleMonth(month));
      });
    return () => {
      cancelled = true;
    };
  }, [monthData.month, selectedDate]);

  useEffect(() => {
    if (!selectedDate) return;
    let cancelled = false;
    const fallback = monthData.days.find((d) => d.visit_date === selectedDate);
    setDayData(fallback || localCastleDay(selectedDate));
    api
      .get("/api/windsor-castle/day", { params: { date: selectedDate } })
      .then((res) => {
        if (!cancelled) setDayData(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          setDayData(fallback || localCastleDay(selectedDate) || { visit_date: selectedDate, is_open: false, open_time: null, close_time: null });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [monthData.days, selectedDate]);

  useEffect(() => {
    const pickup = normalizePostcode(applied.pickup);
    if (!pickup) return;
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      api.get("/api/windsor-castle/distance", { params: { from: pickup } }),
      api.post("/api/quote-preview", {
        pickup,
        dropoff: DEST_POSTCODE,
        passengers: Math.max(1, people || 1),
        bags: Math.max(0, Number(applied.bags || 0)),
        pickup_time: applied.datetime,
      }),
      api.post("/api/windsor-castle/rail-plan", {
        pickup_postcode: pickup,
        depart_at: applied.datetime,
        adults: Math.max(1, Number(applied.adults || 0) + Number(applied.youngPeople || 0) + Number(applied.disabled || 0)),
        children: Math.max(0, Number(applied.children || 0)),
        bags: Math.max(0, Number(applied.bags || 0)),
        return_same_day: Boolean(applied.returnSameDay),
      }),
    ]).then(([distanceRes, taxiRes, railRes]) => {
      if (cancelled) return;
      if (distanceRes.status === "fulfilled" && distanceRes.value.data?.miles) {
        setDistanceMiles(Number(distanceRes.value.data.miles));
      }
      if (taxiRes.status === "fulfilled") {
        setTaxiQuote(selectLiveTaxiPreview(taxiRes.value.data));
      } else {
        setTaxiQuote(null);
      }
      if (railRes.status === "fulfilled") {
        setLiveRailPlan(railRes.value.data?.success ? railRes.value.data : null);
      } else {
        setLiveRailPlan(null);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [applied, people]);

  useEffect(() => {
    if (!dayData?.is_open || !selectedDate) {
      setWeatherSummary(null);
      return;
    }
    const targetLocal = `${selectedDate}T${dayData.open_time || visitTime}`;
    let cancelled = false;
    api
      .get("/api/open/weather/coords", {
        params: { lat: CASTLE_COORDS.lat, lon: CASTLE_COORDS.lon, targetLocal },
      })
      .then((res) => {
        if (!cancelled) setWeatherSummary(res.data?.summary || null);
      })
      .catch(() => {
        if (!cancelled) setWeatherSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [dayData?.is_open, dayData?.open_time, selectedDate, visitTime]);

  useEffect(() => {
    if (!localTransportOpen || !applied.datetime) return;
    let cancelled = false;
    Promise.allSettled(
      LOCAL_POINTS.map((point) =>
        api.post("/api/quote-preview", {
          pickup: point.pickup,
          dropoff: DEST_POSTCODE,
          passengers: Math.max(1, people || 1),
          bags: Math.max(0, Number(applied.bags || 0)),
          pickup_time: applied.datetime,
        })
      )
    ).then((results) => {
      if (cancelled) return;
      const next = {};
      results.forEach((result, idx) => {
        next[LOCAL_POINTS[idx].id] = result.status === "fulfilled" ? selectLiveTaxiPreview(result.value.data) : null;
      });
      setLocalTaxiQuotes(next);
    });
    return () => {
      cancelled = true;
    };
  }, [applied.datetime, applied.bags, localTransportOpen, people]);

  const ticketPricing = useMemo(() => {
    const advanceTotal = ticketTotal(applied, "advance");
    const walkupTotal = ticketTotal(applied, "walkup");
    return {
      advanceTotal,
      walkupTotal,
      saving: Math.max(0, walkupTotal - advanceTotal),
    };
  }, [applied]);

  const modeCosts = useMemo(() => {
    const visitOpen = !!dayData?.is_open;
    const parkingCost = parkingCostForHours(applied.parkingOption, applied.parkingHours, applied.parkingCost);
    const roadCost = estimateRoadCost(distanceMiles, applied.returnSameDay);
    const driveTransport = roadCost + parkingCost;
    const taxiOneWay = taxiQuote?.price != null ? Number(taxiQuote.price) : estimateTaxiFallback(distanceMiles, false);
    const taxiTransport = Math.round(taxiOneWay * (applied.returnSameDay ? 2 : 1));
    const liveRailData = liveRailPlan?.data;
    const trainTransport = liveRailData?.total_public_transport_cost_gbp != null
      ? Number(liveRailData.total_public_transport_cost_gbp)
      : estimateTrainCost(distanceMiles, people);
    const trainDuration = liveRailData?.total_public_transport_duration_mins != null
      ? Number(liveRailData.total_public_transport_duration_mins)
      : Math.round(distanceMiles * 2.4 + 35);
    const trainWalk = Math.max(
      0,
      Number(liveRailData?.access_walk_mins || 0) + Number(liveRailData?.last_mile_walk_mins || 0)
    ) || 12;
    const ticket = visitOpen ? ticketPricing.advanceTotal : 0;
    return {
      drive: {
        transport: driveTransport,
        total: ticket + driveTransport,
        duration: Math.round(distanceMiles * 2.2 + 18),
        walk: 10,
        parking: parkingCost,
        road: roadCost,
        parkingLabel: PARKING_OPTIONS[applied.parkingOption]?.shortLabel || "Parking",
      },
      taxi: { transport: taxiTransport, total: ticket + taxiTransport, duration: Math.round(distanceMiles * 2.0 + 10), walk: 3, live: taxiQuote?.price != null },
      train: {
        transport: trainTransport,
        total: ticket + trainTransport,
        duration: trainDuration,
        walk: trainWalk,
        live: liveRailPlan?.mode && liveRailPlan.mode !== "modelled_fallback",
        gateway: liveRailData?.gateway_crs || null,
        changes: liveRailData?.interchanges ?? null,
        lastMileMode: liveRailData?.egress_mode || null,
        lastMileTaxiLive: Boolean(liveRailData?.last_mile_taxi_live),
      },
    };
  }, [applied.parkingCost, applied.parkingHours, applied.parkingOption, applied.returnSameDay, dayData?.is_open, distanceMiles, liveRailPlan, people, taxiQuote, ticketPricing.advanceTotal]);

  const recommendation = useMemo(() => {
    const entries = Object.entries(modeCosts).sort((a, b) => a[1].total - b[1].total);
    const [key, mode] = entries[0] || ["drive", modeCosts.drive];
    const label = key === "drive" ? "Drive" : key === "taxi" ? "Taxi / private hire" : "Train";
    return { key, label, total: mode.total };
  }, [modeCosts]);

  const timelines = useMemo(() => {
    const target = parseVisitClock(selectedDate, dayData?.open_time || visitTime) || parseVisitClock(selectedDate, visitTime);
    const arriveBy = subtractMinutes(target, 15);
    const build = (duration, walk, middle) => {
      const leave = subtractMinutes(arriveBy, duration);
      return [
        { time: formatClock(leave), label: "Leave home" },
        { time: formatClock(subtractMinutes(arriveBy, walk)), label: middle },
        { time: formatClock(arriveBy), label: "Arrive at Windsor Castle" },
      ];
    };
    return {
      drive: build(modeCosts.drive.duration, modeCosts.drive.walk, "Park / walk"),
      taxi: build(modeCosts.taxi.duration, modeCosts.taxi.walk, "Taxi drop-off"),
      train: build(modeCosts.train.duration, modeCosts.train.walk, "Windsor station"),
    };
  }, [dayData?.open_time, modeCosts.drive.duration, modeCosts.drive.walk, modeCosts.taxi.duration, modeCosts.taxi.walk, modeCosts.train.duration, modeCosts.train.walk, selectedDate, visitTime]);

  const applyInputs = () => {
    setApplied({ ...form, pickup: normalizePostcode(form.pickup) || form.pickup });
  };

  const change = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const numberChange = (field, value, min = 0, max = 20) => {
    const n = Math.max(min, Math.min(max, Number(value) || 0));
    change(field, n);
  };

  const statusText = dayData?.is_open
    ? `Open ${formatTime(dayData.open_time)}-${formatTime(dayData.close_time)}. Last admission ${formatTime(dayData.last_admission_time)}.`
    : dayData?.event_label || "Closed on this date";

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Windsor Castle Budget Planner 2026 | Tickets, Taxi, Rail, Parking</title>
        <meta
          name="description"
          content="Windsor Castle budget and journey planner. Check opening days, ticket prices, taxi cost, rail cost, driving and parking cost, weather and when to leave from your postcode."
        />
        <meta
          name="keywords"
          content="Windsor Castle budget planner, Windsor journey planner, how to get to Windsor Castle, Windsor Castle tickets, how much is Windsor Castle ticket, is Windsor Castle open today, Windsor Castle taxi cost, Windsor Castle rail cost, Windsor Castle driving cost, Windsor Castle parking cost, Windsor Castle weather, taxi cost to Windsor Castle from station, taxi cost to Windsor Castle from hotel, Windsor Castle train cost, Windsor Castle parking prices"
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/windsor-castle-budget-planner" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cheapcabgrab.com/windsor-castle-budget-planner" />
        <meta property="og:title" content="Windsor Castle Budget Planner: Tickets, Taxi, Train and Parking" />
        <meta property="og:description" content="Compare Windsor Castle tickets, opening days, taxi fares, live rail routes, driving costs, parking prices, weather and leave-home time." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Windsor Castle Budget Planner" />
        <meta name="twitter:description" content="Plan how to get to Windsor Castle by taxi, train or car with tickets, parking, weather and live fare checks." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": "https://www.cheapcabgrab.com/windsor-castle-budget-planner#webpage",
                url: "https://www.cheapcabgrab.com/windsor-castle-budget-planner",
                name: "Windsor Castle Budget Planner",
                description:
                  "Compare Windsor Castle ticket prices, opening status, taxi cost, rail cost, driving cost, parking cost, weather and leave-home timing.",
                inLanguage: "en-GB",
                about: { "@id": "https://www.cheapcabgrab.com/windsor-castle-budget-planner#windsor-castle" },
                mainEntity: { "@id": "https://www.cheapcabgrab.com/windsor-castle-budget-planner#faq" },
              },
              {
                "@type": "TouristAttraction",
                "@id": "https://www.cheapcabgrab.com/windsor-castle-budget-planner#windsor-castle",
                name: "Windsor Castle",
                address: {
                  "@type": "PostalAddress",
                  streetAddress: "Windsor Castle",
                  addressLocality: "Windsor",
                  postalCode: DEST_POSTCODE,
                  addressCountry: "UK",
                },
                url: "https://www.cheapcabgrab.com/windsor-castle-budget-planner",
              },
              {
                "@type": "FAQPage",
                "@id": "https://www.cheapcabgrab.com/windsor-castle-budget-planner#faq",
                mainEntity: WINDSOR_FAQS.map((item) => ({
                  "@type": "Question",
                  name: item.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: item.answer,
                  },
                })),
              },
            ],
          })}
        </script>
      </Helmet>

      <LiveStrip />

      <div className="parking-header" style={isMobile ? { marginTop: 6, marginBottom: 8 } : undefined}>
        <h1 style={{ fontSize: isMobile ? 24 : undefined, lineHeight: isMobile ? 1.05 : undefined }}>
          Windsor Castle Budget Planner: Tickets, Taxi, Rail, Parking and Weather
        </h1>
        <div style={{ fontSize: 13, color: "#475467", lineHeight: 1.5 }}>
          <div style={{ fontWeight: 800 }}>Updated: 12th May 2026</div>
          <div>Plan how to get to Windsor Castle from your postcode. Compare ticket cost, taxi cost, rail cost, driving cost, parking cost, weather and leave-home timing.</div>
          <div style={{ marginTop: 4 }}>Book in advance for the best value and to guarantee entry. Children go half price, and each person needs a ticket including children of all ages.</div>
          <div style={{ marginTop: 4, fontWeight: 700 }}>The Castle is normally closed on Tuesdays and Wednesdays.</div>
        </div>
      </div>

      <div className="parking-form" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fffaf7 100%)", border: "1px solid #ead9d3", boxShadow: "0 14px 28px rgba(127,29,29,0.05)" }}>
        <div
          className="option-grid"
          style={{ gap: isMobile ? 10 : 12, alignItems: "flex-start", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}
        >
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: isMobile ? "6px 11px" : "7px 13px",
                  borderRadius: 999,
                  background: "linear-gradient(180deg, #fff2f2 0%, #f7d7d7 100%)",
                  color: "#8f272a",
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                Enter postcode
              </span>
            </label>
            <input
              className="parking-input"
              value={form.pickup}
              onChange={(e) => change("pickup", e.target.value.toUpperCase())}
              placeholder="e.g. SW1A 1AA"
              autoComplete="postal-code"
              style={{ minHeight: isMobile ? 44 : 46, padding: "8px 12px", fontSize: 16 }}
            />
            <div style={{ fontSize: isMobile ? 10.5 : 12, color: "#6b7280", marginTop: 3, lineHeight: 1.28 }}>
              Destination: Windsor Castle, {DEST_POSTCODE}
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: isMobile ? "6px 11px" : "7px 13px",
                  borderRadius: 999,
                  background: "linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%)",
                  color: "#1d4ed8",
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                Visiting date
              </span>
            </label>
            <input
              className="parking-input"
              type="date"
              value={form.datetime ? form.datetime.split("T")[0] : ""}
              onChange={(e) => change("datetime", `${e.target.value}T10:00`)}
              style={{ minHeight: isMobile ? 44 : 46, padding: "8px 12px", fontSize: 16 }}
            />
            <div style={{ fontSize: isMobile ? 10.5 : 12, color: "#6b7280", marginTop: 3, lineHeight: 1.28 }}>
              We use a 10:00 Castle arrival for timing and live fare checks.
            </div>
          </div>
        </div>

        <details className="train-collapsible" style={{ marginTop: isMobile ? 10 : 12 }}>
          <summary className="train-summary-bar" style={isMobile ? { padding: "10px 12px", fontSize: 15, lineHeight: 1.28, background: "linear-gradient(180deg, #fff9f9 0%, #f8fbff 100%)", border: "1px solid #ead9d3" } : { background: "linear-gradient(180deg, #fff9f9 0%, #f8fbff 100%)", border: "1px solid #ead9d3" }}>
            ⚙️ More trip details (optional)
            <span style={{ display: "block", fontSize: isMobile ? 11 : 12, fontWeight: 400, opacity: 0.8, marginTop: 2, lineHeight: isMobile ? 1.28 : undefined }}>
              {hiddenOptionsSummary}
            </span>
          </summary>
          <div className="train-collapsible-body">
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="option-pill return-pill" style={{ marginTop: 0, display: "inline-flex", padding: isMobile ? "6px 10px" : "8px 12px" }}>
                <input type="checkbox" checked={form.returnSameDay} onChange={(e) => change("returnSameDay", e.target.checked)} />
                <span>Return same day</span>
              </label>
            </div>

            <div className="option-grid compact-two" style={{ gap: 10, alignItems: "flex-end" }}>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Adults</label>
                <input className="parking-input" type="number" inputMode="numeric" min="0" max="20" value={form.adults} onChange={(e) => numberChange("adults", e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Young people 18-24</label>
                <input className="parking-input" type="number" inputMode="numeric" min="0" max="20" value={form.youngPeople} onChange={(e) => numberChange("youngPeople", e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Children 5-17</label>
                <input className="parking-input" type="number" inputMode="numeric" min="0" max="20" value={form.children} onChange={(e) => numberChange("children", e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Under 5s</label>
                <input className="parking-input" type="number" inputMode="numeric" min="0" max="20" value={form.under5} onChange={(e) => numberChange("under5", e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Disabled tickets</label>
                <input className="parking-input" type="number" inputMode="numeric" min="0" max="20" value={form.disabled} onChange={(e) => numberChange("disabled", e.target.value)} />
              </div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Bags</label>
                <input className="parking-input" type="number" inputMode="numeric" min="0" max="8" value={form.bags} onChange={(e) => numberChange("bags", e.target.value, 0, 8)} />
              </div>
            </div>

            <div className="option-grid" style={{ marginTop: 10 }}>
              <div className="form-group">
                <label>Travel choice</label>
                <select className="parking-input" value={form.transport} onChange={(e) => change("transport", e.target.value)}>
                  <option value="compare">Compare all</option>
                  <option value="drive">Drive</option>
                  <option value="taxi">Taxi</option>
                  <option value="train">Train</option>
                </select>
                <div style={{ marginTop: 6, color: "#6b7280", fontSize: 12 }}>
                  Distance: {Number(distanceMiles || 0).toFixed(1)} miles.{" "}
                  {liveRailPlan?.mode && liveRailPlan.mode !== "modelled_fallback"
                    ? `Live rail via ${liveRailPlan.data?.gateway_crs || "gateway"} included.`
                    : "Rail uses an estimate until live National Rail data is available."}
                </div>
              </div>
              <div className="form-group">
                <label>Parking option</label>
                <select className="parking-input" value={form.parkingOption} onChange={(e) => change("parkingOption", e.target.value)}>
                  <option value="riverStreet">River Street - near Castle</option>
                  <option value="almaRoad">Alma Road - cheaper, further away</option>
                  <option value="manual">Manual parking budget</option>
                </select>
                <div style={{ marginTop: 6, color: "#6b7280", fontSize: 12, lineHeight: 1.35 }}>
                  {PARKING_OPTIONS[form.parkingOption]?.postcode ? `${PARKING_OPTIONS[form.parkingOption].postcode}. ` : ""}
                  {PARKING_OPTIONS[form.parkingOption]?.note}
                </div>
              </div>
              <div className="form-group">
                <label>{form.parkingOption === "manual" ? "Parking budget" : "Parking stay"}</label>
                {form.parkingOption === "manual" ? (
                  <input className="parking-input" type="number" min="0" value={form.parkingCost} onChange={(e) => numberChange("parkingCost", e.target.value, 0, 80)} />
                ) : (
                  <select className="parking-input" value={form.parkingHours} onChange={(e) => numberChange("parkingHours", e.target.value, 1, 8)}>
                    <option value={1}>Up to 1 hour</option>
                    <option value={2}>1 to 2 hours</option>
                    <option value={3}>2 to 3 hours</option>
                    <option value={4}>3 to 4 hours</option>
                    <option value={5}>4 to 5 hours</option>
                    <option value={6}>Over 5 hours</option>
                  </select>
                )}
                <div style={{ marginTop: 6, color: "#6b7280", fontSize: 12 }}>
                  Parking used in drive cost: {money(parkingCostForHours(form.parkingOption, form.parkingHours, form.parkingCost), 2)}
                </div>
              </div>
            </div>
          </div>
        </details>

        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 12, flexWrap: "wrap", marginTop: isMobile ? 10 : 14 }}>
          <button
            type="button"
            className="parking-btn"
            style={{
              width: "auto",
              minWidth: isMobile ? 0 : 180,
              padding: isMobile ? "8px 12px" : undefined,
              fontSize: isMobile ? 14 : undefined,
              background: "#8f272a",
              borderRadius: isMobile ? 12 : undefined,
            }}
            onClick={applyInputs}
          >
            {hasPendingChanges ? "Show my Windsor Castle plan" : loading ? "Updating..." : "Refresh planner"}
          </button>
          {hasPendingChanges ? (
            <div style={{ fontSize: isMobile ? 12 : 13, color: "#475467", lineHeight: 1.35 }}>
              Inputs changed. Apply to refresh results below.
            </div>
          ) : null}
        </div>
      </div>

      <div className="parking-results" style={{ marginTop: 14, padding: isMobile ? 14 : 20, borderRadius: 20, background: "linear-gradient(180deg, #fffaf7 0%, #f8fbff 100%)", border: "1px solid #ead9d3" }}>
        <div style={{ padding: isMobile ? 12 : 16, borderRadius: 18, background: "linear-gradient(135deg, #7f1d1d 0%, #9f3034 55%, #c78f8f 100%)", color: "#fff" }}>
          <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, color: "rgba(255,255,255,0.78)" }}>
            {dayData?.is_open ? "Castle status" : "Selected date"}
          </div>
          <div style={{ marginTop: 4, fontSize: isMobile ? 18 : 24, fontWeight: 850 }}>
            {dayData?.is_open ? `Windsor Castle is open on ${formatDisplayDate(selectedDate)}` : `Windsor Castle is closed on ${formatDisplayDate(selectedDate) || selectedDate}`}
          </div>
          <div style={{ marginTop: 6, display: "flex", gap: 10, flexWrap: "wrap", fontSize: isMobile ? 12 : 14 }}>
            <span>{statusText}</span>
            <span>Advance tickets {money(ticketPricing.advanceTotal)}</span>
            {ticketPricing.saving > 0 ? <span>Advance saving {money(ticketPricing.saving)}</span> : null}
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: "rgba(255,255,255,0.82)" }}>
            {weatherSummary || "Weather forecast appears when available."}
          </div>
        </div>

        {!dayData?.is_open ? (
          <div className="parking-disclaimer" style={{ marginTop: 12, border: "1px solid #fecaca", background: "#fff1f2" }}>
            Tickets are not available for this date. Tuesdays and Wednesdays are normally closed, and 14-15 June 2026 are listed Castle closures.
          </div>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginTop: 16 }}>
          {["drive", "taxi", "train"]
            .filter((key) => applied.transport === "compare" || applied.transport === key)
            .map((key) => {
              const mode = modeCosts[key];
              const active = recommendation.key === key;
              const title = key === "drive" ? "Drive" : key === "taxi" ? "Taxi / private hire" : "Train";
              const icon = key === "drive" ? "🚗" : key === "taxi" ? "🚕" : "🚆";
              const travelLabel = key === "drive" ? "Road cost" : key === "taxi" ? "Taxi fare" : "Rail + local transfer";
              const cardTone =
                key === "drive"
                  ? { background: "linear-gradient(180deg, #f7fbff 0%, #ffffff 100%)", border: "#bfdbfe", chip: "#dbeafe", chipText: "#1d4ed8" }
                  : key === "taxi"
                  ? { background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "#fde68a", chip: "#fef3c7", chipText: "#92400e" }
                  : { background: "linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)", border: "#bbf7d0", chip: "#dcfce7", chipText: "#166534" };
              return (
                <div
                  key={key}
                  style={{
                    border: active ? "2px solid #8f272a" : `1px solid ${cardTone.border}`,
                    borderRadius: 16,
                    padding: 14,
                    background: cardTone.background,
                    boxShadow: active ? "0 14px 28px rgba(143,39,42,0.10)" : "0 10px 22px rgba(15,23,42,0.05)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <strong>{icon} {title}</strong>
                    {active ? <span style={{ color: "#8f272a", fontWeight: 850 }}>Best value</span> : null}
                  </div>

                  <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                    <span style={{ color: "#475467", fontSize: 13, fontWeight: 800 }}>Total</span>
                    <span style={{ fontSize: 30, fontWeight: 900 }}>{money(mode.total)}</span>
                  </div>

                  <div style={{ marginTop: 10, display: "grid", gap: 7, padding: 10, borderRadius: 12, background: "rgba(255,255,255,0.82)", border: "1px solid rgba(148,163,184,0.28)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                      <span style={{ color: "#475467" }}>Tickets <span style={{ color: "#64748b" }}>({ticketPartySummary || "selected visitors"})</span></span>
                      <strong>{money(ticketPricing.advanceTotal)}</strong>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                      <span style={{ color: "#475467" }}>{travelLabel}</span>
                      <strong>{money(key === "drive" ? mode.road : mode.transport)}</strong>
                    </div>
                    {key === "drive" ? (
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
                        <span style={{ color: "#475467" }}>Parking <span style={{ color: "#64748b" }}>({mode.parkingLabel})</span></span>
                        <strong>{money(mode.parking, 2)}</strong>
                      </div>
                    ) : null}
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13, paddingTop: 7, borderTop: "1px solid #e5e7eb" }}>
                      <span style={{ color: "#0f172a", fontWeight: 800 }}>Total day cost</span>
                      <strong>{money(mode.total)}</strong>
                    </div>
                  </div>

                  <div style={{ marginTop: 9, display: "inline-flex", padding: "5px 8px", borderRadius: 999, background: cardTone.chip, color: cardTone.chipText, fontSize: 12, fontWeight: 800 }}>
                    {mode.duration} mins door-to-Castle • {mode.walk} mins walk
                  </div>
                  {key === "taxi" && mode.live ? <div style={{ marginTop: 7, fontSize: 12, color: "#166534", fontWeight: 800 }}>Live taxi preview fare included</div> : null}
                  {key === "train" && mode.live ? (
                    <div style={{ marginTop: 7, fontSize: 12, color: "#166534", fontWeight: 800 }}>
                      Live rail via {mode.gateway}{mode.changes != null ? ` • ${mode.changes} change${mode.changes === 1 ? "" : "s"}` : ""}
                      {mode.lastMileMode === "taxi" ? ` • ${mode.lastMileTaxiLive ? "live" : "estimated"} station taxi` : " • walk to Castle"}
                    </div>
                  ) : null}

                  <div style={{ marginTop: 12, padding: 10, borderRadius: 12, background: "#ffffff", border: "1px solid rgba(148,163,184,0.30)", display: "grid", gap: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 850, color: "#334155" }}>Leave-time plan</div>
                    {timelines[key].map((step) => (
                      <div key={`${key}-${step.label}`} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12 }}>
                        <span style={{ color: "#64748b" }}>{step.label}</span>
                        <strong>{step.time}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      <details className="train-collapsible" style={{ marginTop: 14 }}>
        <summary className="train-summary-bar">🎟️ Ticket prices and key visitor notes</summary>
        <div className="train-collapsible-body">
          <ul>
            <li>Book in advance for the best value and to guarantee entry.</li>
            <li>Your ticket includes a multimedia guide.</li>
            <li>Children go half price. Under 5s and access companions are free, but each person should still have a ticket.</li>
            <li>St George's Chapel is not open to visitors on Sundays, when it is only open for worship.</li>
            <li>Tickets can be converted to a 1-Year Pass. A £2 transaction fee applies when pre-booking a return visit.</li>
            <li>£1 tickets are available for those on Universal Credit and other named UK benefits.</li>
            <li>Royal Borough of Windsor and Maidenhead Advantage Card holders can obtain 50% off admission.</li>
          </ul>
          <div style={{ overflowX: "auto", marginTop: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px 10px 8px 0" }}>Ticket</th>
                  <th style={{ textAlign: "left", padding: "8px 10px" }}>Advance</th>
                  <th style={{ textAlign: "left", padding: "8px 10px" }}>On the day</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(CASTLE_TICKETS).map((ticket) => (
                  <tr key={ticket.label} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "8px 10px 8px 0" }}>{ticket.label}</td>
                    <td style={{ padding: "8px 10px" }}>{ticket.advance === 0 ? "Free" : money(ticket.advance, 2)}</td>
                    <td style={{ padding: "8px 10px" }}>{ticket.walkup === 0 ? "Free" : money(ticket.walkup, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </details>

      <details className="train-collapsible">
        <summary className="train-summary-bar">🅿️ Windsor Castle parking used in drive costs</summary>
        <div className="train-collapsible-body">
          <p style={{ marginTop: 0 }}>
            Drive estimates include the selected parking option. Disabled badge holders can park free in these car parks when displaying a valid disabled badge.
          </p>
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
            {["riverStreet", "almaRoad"].map((key) => {
              const option = PARKING_OPTIONS[key];
              return (
                <div key={key} style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
                  <strong>{option.label}</strong>
                  <div style={{ marginTop: 4, fontSize: 13, color: "#475467" }}>{option.postcode} • {option.note}</div>
                  <div style={{ marginTop: 8, display: "grid", gap: 5, fontSize: 13 }}>
                    {option.tariffs.map((tier, idx) => (
                      <div key={`${key}-${idx}`} style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <span style={{ color: "#64748b" }}>{idx === 0 ? "Up to 1 hour" : tier.maxHours === Infinity ? "Over 5 hours" : `${idx} to ${idx + 1} hours`}</span>
                        <strong>{money(tier.price, 2)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </details>

      <details
        ref={localTransportRef}
        className="train-collapsible"
        open={localTransportOpen}
        onToggle={(e) => setLocalTransportOpen(e.currentTarget.open)}
      >
        <summary className="train-summary-bar">🚕 Live taxi checks from Windsor hotels, stations and Heathrow</summary>
        <div className="train-collapsible-body">
          <p style={{ marginTop: 0 }}>
            Most central Windsor hotels are walkable to the Castle, but live taxi checks help when you have bags, children, mobility needs or a tight return plan.
          </p>
          <div style={{ display: "grid", gap: 10 }}>
            {LOCAL_POINTS.map((point) => {
              const quote = localTaxiQuotes[point.id];
              return (
                <div key={point.id} style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 10, background: "#fff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <strong>{point.label}</strong>
                    <span style={{ fontSize: 12, fontWeight: 800, color: "#8f272a" }}>{point.kind}</span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, color: "#475467" }}>{point.pickup} • {point.duration} • {point.note}</div>
                  <div style={{ marginTop: 4, fontSize: 13 }}>
                    Taxi preview to Castle: {quote?.price != null ? `${money(quote.price, 2)} (${String(quote.code || "car").toUpperCase()})` : "open this section to check live price"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </details>

      <details className="train-collapsible">
        <summary className="train-summary-bar">ℹ️ Opening rules used by this v0.1 planner</summary>
        <div className="train-collapsible-body">
          <ul>
            <li>Normally closed on Tuesdays and Wednesdays.</li>
            <li>1 November to 28 February: 10:00 opening, 15:00 last admission, 16:15 close.</li>
            <li>1 March to 31 October: 10:00 opening, 16:00 last admission, 17:15 close.</li>
            <li>14 May 2026: early close 16:45, last admission 15:30.</li>
            <li>14 and 15 June 2026: Castle closed.</li>
            <li>Semi-State Rooms are currently closed until autumn 2026.</li>
          </ul>
        </div>
      </details>

      <details className="train-collapsible">
        <summary className="train-summary-bar">❓ Windsor Castle journey planner FAQs</summary>
        <div className="train-collapsible-body">
          <div style={{ display: "grid", gap: 10 }}>
            {WINDSOR_FAQS.map((item) => (
              <div key={item.question} style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
                <h2 style={{ margin: 0, fontSize: 16, lineHeight: 1.25 }}>{item.question}</h2>
                <p style={{ margin: "6px 0 0", color: "#475467", fontSize: 13, lineHeight: 1.45 }}>{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </details>

      <ReviewsBar pageKey="/windsor-castle-budget-planner" />

      <div className="parking-disclaimer" style={{ marginTop: 12 }}>
        Data sources: Windsor Castle public visitor information supplied in the planner brief; taxi pricing uses live CheapCabGrab preview fares where available; rail and drive costs are v0.1 estimates. Always verify opening and tickets before travelling. Updated: 12th May 2026.
      </div>

      <div style={{ marginTop: 20, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
        <strong>Plan your Windsor Castle visit:</strong>
        <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
          <li>🎢 <a href="/legoland-budget-planner">Legoland Budget Planner</a> — compare tickets, taxi, rail, parking and total day cost</li>
          <li>🎢 <a href="/thorpepark-budget-planner">Thorpe Park Day Planner</a> — compare taxi, rail, driving and tickets</li>
          <li>🆚 <a href="/legoland-vs-thorpe-park">LEGOLAND vs Thorpe Park (compare)</a></li>
          <li>🚕 <a href="/windsor-taxi">Windsor taxi</a> — local station, hotel and Castle transfers</li>
          <li>🚕 <a href="/slough-taxi">Slough taxi</a> — Slough station to Windsor Castle transfer</li>
          <li>✈️ <a href="/heathrow-to-windsor">Heathrow to Windsor</a> — airport taxi and transfer guidance</li>
          <li>🚆 <a href="/train-vs-taxi">Train vs Taxi (national)</a></li>
          <li>ℹ️ <a href="https://www.rct.uk/visit/windsor-castle" target="_blank" rel="noreferrer">Windsor Castle official visitor information</a></li>
          <li>🚇 <a href="https://tfl.gov.uk" target="_blank" rel="noreferrer">TfL journey planner</a></li>
          <li>🚆 <a href="https://www.nationalrail.co.uk" target="_blank" rel="noreferrer">National Rail</a></li>
        </ul>
      </div>
    </div>
  );
}
